import React, { useCallback, useImperativeHandle, useState } from "react";
import type { Ref } from "react";
import { isFunction } from "@common/helpers/typeGuards";
import { OAuthServiceType } from "@common/constants";
import { Entity } from "@common/types/entity";
import { IntegrationConnection } from "@common/types/integrationConnection";
import { useWindowMessage } from "@hooks/useWindowMessage";
import { useInteraction } from "@hooks/useInteraction";
import { OAuthAuthorizationResponse, triggerAuthorization } from "@lib/oauth";
import { useMercuryConnectionCreationMutation } from "@api/mercury.api";
import {
    ConnectionActionButton,
    ConnectionButtonSize,
    ConnectionButtonVariant,
} from "@components/financialConnection/ConnectionActionButton";
import { useViewOnlyRefusal } from "@hooks/permissions/useViewOnlyRefusal";
import { CreateAccountHandle } from "../accounts/AddAccounts/lib";

interface Props {
    children?:
        | React.ReactNode
        | ((props: {
              connecting: boolean;
              initializeConnection: () => void;
          }) => React.ReactNode);
    btnVariant?: ConnectionButtonVariant;
    btnSize?: ConnectionButtonSize;
    onCreated: (connection: IntegrationConnection) => void;
    defaultEntity?: Entity;
}
export const CreateMercuryConnection = ({
    children = <>Connect</>,
    onCreated,
    btnVariant = "secondary",
    btnSize = "sm",
    defaultEntity,
    ref,
}: Props & { ref?: Ref<CreateAccountHandle> }) => {
    const mutation = useMercuryConnectionCreationMutation(onCreated);
    const [creating, setCreating] = useState(false);
    const handler = ({ code }: OAuthAuthorizationResponse) => {
        if (creating) {
            mutation.mutate({
                authCode: code,
                defaultEntityId: defaultEntity?.id,
            });
        }
        setCreating(false);
    };

    useWindowMessage<OAuthAuthorizationResponse>({
        scope: OAuthServiceType.MERCURY,
        handler,
    });

    const { refuseIfViewOnly } = useViewOnlyRefusal();

    const initializeConnection = useCallback(() => {
        if (creating) {
            return;
        }

        // The next step leaves the app, so the server never gets to refuse
        // it. Refuse here instead of after the provider's consent screen.
        if (refuseIfViewOnly()) {
            return;
        }

        triggerAuthorization(OAuthServiceType.MERCURY);
        setCreating(true);
    }, [creating, refuseIfViewOnly]);

    const authorize = useInteraction(initializeConnection);

    useImperativeHandle(
        ref,
        () => ({
            initializeConnection,
        }),
        [initializeConnection],
    );

    if (isFunction(children)) {
        return children({ connecting: creating, initializeConnection });
    }

    return (
        <span {...authorize}>
            <ConnectionActionButton
                loading={mutation.isPending}
                variant={btnVariant}
                size={btnSize}
            >
                {children}
            </ConnectionActionButton>
        </span>
    );
};
